import { useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { IconFilter, IconShoppingBag } from "@tabler/icons-react";
import toast from "react-hot-toast";

import { Heading, Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card/section-card";
import { SearchBar } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button";
import { Drawer } from "../../../../components/common/drawer";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { ConfirmDialog } from "../../../../components/common/confirm-dialog";
import { adminFeaturedDealsService, adminProductsService } from "../../../api_client";
import type {
  FeaturedDealDto,
  ProductDto,
  ProductsQueryDto,
  ProductsResponseDto,
} from "../../../../api";
import { useAdminFeaturedDeals } from "../../hooks/useFeaturedDeals";
import { useApi } from "../../../../swr/useApi";
import { useSearchFiltersUrlSync } from "../../../pagination/hooks/useSearchFiltersUrlSync";
import { usePaginationSortingUrlSync } from "../../../pagination/hooks/usePaginationSortingUrlSync";
import { ProductsFilterContent } from "../../../products/presentation/components/products-filter-content";
import { SortableDealRow } from "../components/sortable-deal-row";
import { AdminDealSearchRow } from "../components/admin-deal-search-row";

const PAGE_SIZE = 10;

export function AdminFeaturedDealsPage() {
  const { data, isLoading, error, mutate } = useAdminFeaturedDeals();
  const deals = useMemo(() => data ?? [], [data]);
  const featuredTemplateIds = useMemo(
    () => new Set(deals.map((d) => d.productTemplateId)),
    [deals],
  );

  const { searchValue, setSearchValue, filters, setFilters, baseQuery } =
    useSearchFiltersUrlSync<ProductsQueryDto>({
      initialFilters: {} as Record<string, unknown>,
      enableSearch: true,
    });

  const { pagination, setPagination, sortingQuery } = usePaginationSortingUrlSync({
    defaultPageSize: PAGE_SIZE,
  });

  const [filterOpen, setFilterOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<FeaturedDealDto | null>(null);

  const productsQuery: ProductsQueryDto = {
    ...(baseQuery as ProductsQueryDto),
    ...sortingQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const queryKey = `admin-deal-product-search-${JSON.stringify(productsQuery)}`;
  const { data: searchResults, isLoading: searchLoading } = useApi<ProductsResponseDto>(
    queryKey,
    () => adminProductsService.adminGetProducts(productsQuery),
  );
  const products = searchResults?.data ?? [];
  const totalMatches = searchResults?.meta?.totalCount ?? 0;
  const pageCount = searchResults?.meta?.pageCount ?? 1;

  const activeFilterCount = Object.values(filters as Record<string, unknown>).filter(
    (v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
  ).length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = deals.findIndex((d) => d.id === active.id);
    const newIndex = deals.findIndex((d) => d.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(deals, oldIndex, newIndex);
    await mutate(next, { revalidate: false });
    setReordering(true);
    try {
      await adminFeaturedDealsService.adminReorderFeaturedDeals({
        orderedIds: next.map((d) => d.id),
      });
      await mutate();
    } catch (err) {
      toast.error("Failed to save new order");
      await mutate();
      throw err;
    } finally {
      setReordering(false);
    }
  }

  async function handleAdd(product: ProductDto) {
    setBusyId(product.id);
    try {
      await adminFeaturedDealsService.adminAddFeaturedDeal({ productTemplateId: product.id });
      toast.success(`Added “${product.displayName ?? product.name}” to Deal of the Week`);
      await mutate();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Failed to add product";
      toast.error(message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(id: number) {
    setBusyId(id);
    try {
      await adminFeaturedDealsService.adminRemoveFeaturedDeal(id);
      await mutate();
      setPendingRemoval(null);
    } catch {
      toast.error("Failed to remove deal");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      <div className="flex flex-col gap-2">
        <Heading variant="h2" tone="dark">Deal of the Week</Heading>
        <Text variant="bodyLg" tone="muted">
          Curate which products appear in the Deal of the Week row on the home page, landing page,
          and catalog. Drag to reorder.
        </Text>
      </div>

      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <aside className="w-full shrink-0 xl:sticky xl:top-6 xl:w-[480px]">
          <SectionCard
            shadow
            title="Current selection"
            subtitle={`${deals.length} product${deals.length === 1 ? "" : "s"} featured`}
            className="gap-4"
          >
            {isLoading ? (
              <ShimmerLoader message="Loading current selection…" lines={3} />
            ) : error ? (
              <Text variant="body" tone="muted">Failed to load Deal of the Week list.</Text>
            ) : deals.length === 0 ? (
              <Text variant="body" tone="muted">
                No products selected yet. Browse the catalog to add the first one.
              </Text>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={deals.map((d) => d.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto pr-1">
                    {deals.map((deal: FeaturedDealDto) => (
                      <SortableDealRow
                        key={deal.id}
                        deal={deal}
                        onRemove={(id) =>
                          setPendingRemoval(deals.find((d) => d.id === id) ?? null)
                        }
                        disabled={reordering || busyId === deal.id}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </SectionCard>
        </aside>

        <div className="min-w-0 flex-1">
          <SectionCard
            shadow
            title="Browse catalog"
            subtitle="All products are listed. Use search or filters to narrow down."
            action={
              <Button
                variant="secondary"
                size="md"
                leftIcon={<IconFilter size={16} />}
                onClick={() => setFilterOpen(true)}
              >
                Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </Button>
            }
            className="gap-5"
          >
            <SearchBar
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search by product name, SKU, or brand…"
              showSearchButton={false}
            />

            {searchLoading ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-4"
                  >
                    <Shimmer className="h-16 w-16 rounded-lg" />
                    <div className="flex flex-1 flex-col gap-2">
                      <Shimmer className="h-4 w-1/2 rounded" />
                      <Shimmer className="h-3 w-1/3 rounded" />
                    </div>
                    <Shimmer className="h-9 w-24 rounded-xl" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-borderDefault bg-bgWhite p-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bgLight">
                  <IconShoppingBag size={32} className="text-textMuted" />
                </div>
                <Text variant="bodyBold" tone="dark">No products found</Text>
                <Text variant="bodySm" tone="muted">
                  Try a different search term or clear the filters.
                </Text>
              </div>
            ) : (
              <>
                <Text variant="bodySm" tone="muted">
                  {products.length === totalMatches
                    ? `${totalMatches} product${totalMatches === 1 ? "" : "s"}`
                    : `Showing ${(pagination.pageIndex * pagination.pageSize) + 1}–${(pagination.pageIndex * pagination.pageSize) + products.length} of ${totalMatches} products`}
                </Text>
                <div className="flex flex-col gap-3">
                  {products.map((product) => (
                    <AdminDealSearchRow
                      key={product.id}
                      product={product}
                      alreadyFeatured={featuredTemplateIds.has(product.id)}
                      busy={busyId === product.id}
                      onAdd={handleAdd}
                    />
                  ))}
                </div>

                {pageCount > 1 && (
                  <div className="mt-2 flex justify-center border-t border-borderDefault pt-4">
                    <Pagination
                      page={pagination.pageIndex + 1}
                      pageCount={pageCount}
                      onPageChange={(page) =>
                        setPagination({ ...pagination, pageIndex: page - 1 })
                      }
                    />
                  </div>
                )}
              </>
            )}
          </SectionCard>
        </div>
      </div>

      <Drawer
        open={filterOpen}
        close={() => setFilterOpen(false)}
        title="Filter Products"
        content={ProductsFilterContent({
          filters: filters as ProductsQueryDto,
          onFiltersChange: (f) => {
            setFilters(f);
            setFilterOpen(false);
          },
        })}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        onOpenChange={(o) => {
          if (!o) setPendingRemoval(null);
        }}
        onConfirm={() => pendingRemoval && handleRemove(pendingRemoval.id)}
        loading={pendingRemoval !== null && busyId === pendingRemoval.id}
        title="Remove from Deal of the Week?"
        description={`“${
          pendingRemoval?.product?.displayName ??
          pendingRemoval?.product?.name ??
          "This product"
        }” will no longer appear in the Deal of the Week row. You can add it back any time.`}
        confirmText="Remove"
      />
    </div>
  );
}
